import { useRef } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  TEST_TRANSPORT_GQL,
  SUBSCRIBE_EMAIL_NOTIFICATION_STATUS,
  SUBSCRIBE_SMS_NOTIFICATION_STATUS,
  SUBSCRIBE_PUSH_NOTIFICATION_STATUS,
} from './queries';
import { mapTransportStatus, pickMostImportantNotificationStatus } from '../../utils/transportStatus';

const FEEDFLO_SMS_NUMBER = '(204) 262-1928';
const phoneNumberDigits = FEEDFLO_SMS_NUMBER.replace(/\D/g, '');

function useTransportTesting({ userId, onStatusUpdate, onPendingTestUpdate }) {
  // --- Refs for subscription management ---
  const subscriptionRefs = useRef({});
  const toastIdsRef = useRef({});

  // --- Apollo/GraphQL hooks ---
  const client = useApolloClient();
  const [testTransport] = useMutation(TEST_TRANSPORT_GQL);

  // --- Function definitions ---
  const cleanupSubscription = (transportType) => {
    if (subscriptionRefs.current[transportType]) {
      subscriptionRefs.current[transportType].unsubscribe();
      delete subscriptionRefs.current[transportType];
    }
  };

  const handleTestTransport = async (transportType) => {
    // Show initial toast
    const sendingMessages = {
      sms: 'Sending SMS...',
      push: 'Sending push notification...',
      email: 'Sending email...',
    };

    const toastId = toast.info(sendingMessages[transportType] || `Sending ${transportType}...`, {
      position: 'top-right',
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
    });

    // Store toast ID in ref for subscription access
    toastIdsRef.current[transportType] = toastId;

    // Mark this transport type as having a pending test
    onPendingTestUpdate?.((prev) => ({
      ...prev,
      [transportType]: { toastId, startTime: Date.now() },
    }));

    try {
      // Step 1: Call the test mutation
      const result = await testTransport({
        variables: {
          transportType: transportType,
          userId: userId,
        },
      });

      const messageId = result?.data?.user_notification_transport_type_test?.messageId;
      const errorMessage = result?.data?.user_notification_transport_type_test?.data?.error;

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (!messageId) {
        throw new Error('No message ID returned from test');
      }

      // Step 2: Subscribe to status updates for this specific message
      const subscriptionQueries = {
        email: SUBSCRIBE_EMAIL_NOTIFICATION_STATUS,
        sms: SUBSCRIBE_SMS_NOTIFICATION_STATUS,
        push: SUBSCRIBE_PUSH_NOTIFICATION_STATUS,
      };

      const subscription = client
        .subscribe({
          query: subscriptionQueries[transportType],
          variables: { messageId },
        })
        .subscribe({
          next: ({ data }) => {

            // Push notifications use a different table name
            const statusKey =
              transportType === 'push' ? 'push_notification_receipt' : `${transportType}_notification_status`;
            const statuses = data?.[statusKey];

            if (!statuses || statuses.length === 0) return;

            const normalizedStatuses =
              transportType === 'push'
                ? statuses.map((receipt) => ({
                  ...receipt,
                  status: receipt.status,
                  occurred_at: receipt.occurred_at,
                }))
                : statuses;

            const latestStatus = pickMostImportantNotificationStatus(transportType, normalizedStatuses);
            if (!latestStatus) return;

            const mappedStatus = mapTransportStatus(transportType, latestStatus.status);

            // Update transport status
            onStatusUpdate?.((prev) => {
              // Determine target address based on transport type
              let targetAddress = latestStatus.message?.sent_to || prev[transportType]?.targetAddress;
              // For push notifications, use phone info if available
              if (transportType === 'push' && latestStatus.token?.phone_info) {
                const phoneInfo = latestStatus.token.phone_info;
                if (phoneInfo.manufacturer && phoneInfo.model) {
                  targetAddress = `${phoneInfo.manufacturer} ${phoneInfo.model}`;
                } else if (phoneInfo.manufacturer) {
                  targetAddress = phoneInfo.manufacturer;
                } else if (phoneInfo.model) {
                  targetAddress = phoneInfo.model;
                }
              }

              return {
                ...prev,
                [transportType]: {
                  targetAddress,
                  // checked might seem weird, but in concert with status I think it's decent. pending will say pending after all
                  // if we used completed_at it would have to wait until the status got checked again
                  sent_at: latestStatus.occurred_at,
                  status: mappedStatus,
                },
              };
            });

            // Handle toast notifications based on mapped status
            const transportLabel = {
              sms: 'SMS',
              email: 'Email',
              push: 'Push',
            }[transportType];

            if (mappedStatus === 'success') {
              toast.dismiss(toastIdsRef.current[transportType]);
              toast.success(`${transportLabel} delivered successfully!`, {
                position: 'top-right',
                autoClose: 3000,
              });
              delete toastIdsRef.current[transportType];
              cleanupSubscription(transportType);
              onPendingTestUpdate?.((prev) => {
                const newPending = { ...prev };
                delete newPending[transportType];
                return newPending;
              });
            } else if (mappedStatus === 'pending') {
              toast.dismiss(toastIdsRef.current[transportType]);
              const warningToastId = toast.warning(`${transportLabel} sent, awaiting delivery confirmation...`, {
                position: 'top-right',
                autoClose: false,
              });
              toastIdsRef.current[transportType] = warningToastId;
              onPendingTestUpdate?.((prev) => ({
                ...prev,
                [transportType]: { ...prev[transportType], toastId: warningToastId },
              }));
            } else if (mappedStatus === 'error') {
              toast.dismiss(toastIdsRef.current[transportType]);
              toast.error(`${transportLabel} failed: ${latestStatus.status}`, {
                position: 'top-right',
                autoClose: 4000,
              });
              delete toastIdsRef.current[transportType];

              cleanupSubscription(transportType);
              onPendingTestUpdate?.((prev) => {
                const newPending = { ...prev };
                delete newPending[transportType];
                return newPending;
              });
            } else if (mappedStatus === 'unsubscribed') {
              toast.dismiss(toastIdsRef.current[transportType]);

              if (transportType === 'sms') {
                toast.error(
                  <span>
                    SMS notifications are currently disabled for this phone number. To enable notifications, text
                    &quot;START&quot; to{' '}
                    <a href={`sms://${phoneNumberDigits}?body=START`} style={{ textDecoration: 'underline' }}>
                      {FEEDFLO_SMS_NUMBER}
                    </a>
                    .
                  </span>,
                  {
                    position: 'top-right',
                    autoClose: 30000,
                    hideProgressBar: false,
                    closeOnClick: true,
                  },
                );
              } else {
                toast.error(`${transportLabel} unsubscribed`, {
                  position: 'top-right',
                  autoClose: 4000,
                });
              }

              delete toastIdsRef.current[transportType];
              cleanupSubscription(transportType);
              onPendingTestUpdate?.((prev) => {
                const newPending = { ...prev };
                delete newPending[transportType];
                return newPending;
              });
            }
          },
          error: (error) => {
            console.error(`Subscription error for ${transportType}:`, error);
            toast.dismiss(toastIdsRef.current[transportType]);
            toast.error(`Subscription error: ${error.message}`, {
              position: 'top-right',
              autoClose: 4000,
            });
            delete toastIdsRef.current[transportType];
            cleanupSubscription(transportType);
            onPendingTestUpdate?.((prev) => {
              const newPending = { ...prev };
              delete newPending[transportType];
              return newPending;
            });
          },
        });

      // Store the subscription for cleanup
      subscriptionRefs.current[transportType] = subscription;
    } catch (error) {
      toast.dismiss(toastIdsRef.current[transportType]);
      toast.error(`Error sending ${transportType}. Test failed.`, {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      delete toastIdsRef.current[transportType];
      onPendingTestUpdate?.((prev) => {
        const newPending = { ...prev };
        delete newPending[transportType];
        return newPending;
      });
    }
  };

  const cleanupAllSubscriptions = () => {
    Object.keys(subscriptionRefs.current).forEach((key) => {
      cleanupSubscription(key);
    });
  };

  return {
    handleTestTransport,
    cleanupAllSubscriptions,
  };
}

export default useTransportTesting;
